// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Card, Flex, Image, Typography } from '@iheartjane/reefer';
import type { ColorKey, MarginProperties } from '@iheartjane/reefer';
import type { LabeledOption } from '@iheartjane/types';
import type { MaskitoOptions } from '@maskito/core';
import { useMaskito } from '@maskito/react';
import snakeCase from 'lodash-es/snakeCase';

import { alwaysStartsWith } from '@jane/business-admin/util';

import styles from './ColorPicker.module.css';
import colorWheel from './color-wheel.png';

export interface ColorPickerProps extends LabeledOption, MarginProperties {
  hasError?: boolean;
  labelAlign?: 'center' | 'left';
  labelColor?: ColorKey;
  onBlur?: () => void;
  onChange: (hex: string) => void;
  shouldStretch?: boolean;
}

const ColorPreview = styled(Card.Content)<{ hexColor: string }>(
  ({ theme, hexColor }) => ({
    cursor: 'crosshair',
    backgroundColor: hexColor,
    borderTopLeftRadius: theme.borderRadius.sm,
    borderTopRightRadius: theme.borderRadius.sm,
    minHeight: '88px',
    minWidth: '147px',
  })
);

const HexField = styled('input')<{
  hasError?: boolean;
  shouldStretch?: boolean;
}>(({ theme, hasError, shouldStretch = false }) => ({
  border: `solid ${hasError ? '#F33' : theme.colors.grays.light} 1px`,
  borderRadius: '12px',
  padding: '12px',
  width: shouldStretch ? '100%' : 'auto',
}));

const ColorPickerContainer = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.grays.white,
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
}));

const ColorIconContainer = styled(Flex)(({ theme }) => ({
  cursor: 'pointer',
  height: '48px',
  width: '46px',
  paddingLeft: '11px',
  paddingRight: '11px',
  paddingTop: '12px',
  paddingBottom: '12px',
  borderRadius: '12px',
  '&:hover': {
    backgroundColor: theme.colors.grays.ultralight,
  },
  justifyContent: 'center',
  alignItems: 'center',
}));

const ColorContentContainer = styled(Flex)(({ theme }) => ({
  borderTop: `1px solid ${theme.colors.grays.ultralight}`,
  paddingTop: '12px',
  paddingBottom: '16px',
  paddingLeft: '8px',
  paddingRight: '16px',
  gap: '8px',
}));

const hexColorMask: MaskitoOptions = {
  mask: /^#[0-9a-fA-F]{0,6}$/,
  preprocessors: [alwaysStartsWith('#')],
};

export const ColorPicker = ({
  label,
  labelAlign = 'center',
  labelColor = 'grays-mid',
  onBlur,
  onChange,
  value,
  hasError,
  shouldStretch = false,
  ...marginProps
}: ColorPickerProps) => {
  const inputRef = useMaskito({ options: hexColorMask });
  const inputId = snakeCase(label);
  return (
    <Flex
      alignItems="center"
      flexDirection="column"
      mr={24}
      {...marginProps}
      width={shouldStretch ? '100%' : 'auto'}
    >
      <label
        htmlFor={inputId}
        style={{ width: shouldStretch ? '100%' : 'auto' }}
      >
        <Typography
          textAlign={labelAlign}
          variant="mini-bold"
          color={labelColor}
          mb={8}
        >
          {label}
        </Typography>
        <Card.Group width={shouldStretch ? '100%' : '177px'} height="136px">
          <ColorPreview hexColor={value}>&nbsp;</ColorPreview>
          <ColorContentContainer>
            <ColorPickerContainer>
              <ColorIconContainer>
                <Image
                  altText="color-wheel"
                  src={colorWheel}
                  width="24px"
                  height="24px"
                />
              </ColorIconContainer>
              <HexField
                ref={inputRef}
                type="text"
                onInput={(e) => onChange(e.currentTarget.value)}
                onBlur={onBlur}
                value={value}
                maxLength={7}
                size={9}
                hasError={hasError}
                shouldStretch={shouldStretch}
              />
            </ColorPickerContainer>
            <input
              id={inputId}
              className={styles['colorField']}
              type="color"
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          </ColorContentContainer>
        </Card.Group>
      </label>
    </Flex>
  );
};
